import type { TFunction } from 'i18next';
import { ChevronDown, ChevronUp, Home } from '@tonnie/assets';
import { Badge, Card, cn } from '@tonnie/ui';
import { AnimatePresence, motion } from 'framer-motion';
import { TaskCard } from './TaskCard';
import { TRACK_ICONS, trackVisual, type KitTask, type KitTrack } from './types';

export function TrackSection({
  track,
  tasks,
  onRemoveTask,
  canRemove,
  getDependencyWarning,
  isExpanded,
  onToggleExpand,
  t,
}: {
  track: KitTrack;
  tasks: KitTask[];
  onRemoveTask: (code: string) => void;
  canRemove: (task: KitTask) => boolean;
  getDependencyWarning: (code: string) => string | null;
  isExpanded: boolean;
  onToggleExpand: () => void;
  t: TFunction;
}) {
  const Icon = TRACK_ICONS[track.track_code] ?? Home;
  const visual = trackVisual(track.track_code);

  return (
    <Card>
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-2">
          <Icon className={cn('w-4 h-4', visual?.color ?? 'text-muted-foreground')} />
          <span className="font-semibold">
            {visual ? t(visual.nameKey) : t(track.track_name_key)}
          </span>
          <Badge variant="secondary" className="text-xs">
            {tasks.length}
          </Badge>
        </div>
        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 space-y-1.5">
              {tasks.map((task) => (
                <TaskCard
                  key={task.task_code}
                  task={task}
                  onRemove={() => onRemoveTask(task.task_code)}
                  canRemove={canRemove(task)}
                  dependencyWarning={getDependencyWarning(task.task_code)}
                  t={t}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}
